import { Link, List, ListItem, Stack, styled, Switch, Typography } from '@mui/material';
import React from 'react';
import { SETTINGS_ITEM_MAX_WIDTH } from 'themes/constants';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { NotificationSettingsData } from 'features/settings/types/types';
import { PhoneInput } from 'components/PhoneInput/PhoneInput';
import { NotificationType } from 'features/settings/types/notificationTypes';

// TODO: Replace with actual notification settings content translation keys
// https://wellframe.atlassian.net/browse/WFMOB-14452
// TODO ticket to update translations for sms_subtext_2
interface DisabledOverlayProps {
  enabledInExternalSystem: boolean;
}

const StyledContainer = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  maxWidth: SETTINGS_ITEM_MAX_WIDTH,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
}));
const DisabledOverlay = styled(List, {
  shouldForwardProp: (prop) => prop !== 'enabledInExternalSystem',
})<DisabledOverlayProps>(({ enabledInExternalSystem }) => ({
  opacity: enabledInExternalSystem ? 1 : 0.5,
  pointerEvents: enabledInExternalSystem ? 'auto' : 'none',
}));

const StyledListItem = styled(ListItem)(() => ({
  paddingBlock: 0,
}));

const Footnote = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
}));
const PhoneInputStack = styled(Stack)(({ theme }) => ({
  maxWidth: '100%',
  [theme.breakpoints.up('md')]: {
    maxWidth: '75%',
  },
}));

interface NotificationSettingsProps extends NotificationSettingsData {
  onChangeNotificationPreferences: (
    event: React.ChangeEvent<HTMLInputElement>,
    notificationType?: NotificationType,
  ) => void;
  chatNotificationsEnabled?: boolean;
  memberNotificationEnabled?: boolean;
  memberSmsEnabled?: boolean;
  memberLoaded?: boolean;
}

export const NotificationPreferencesContent = ({
  enabledInExternalSystem,
  userPhoneNumber,
  enabled,
  chatNotificationsEnabled,
  memberNotificationEnabled,
  memberSmsEnabled,
  memberLoaded,
  hospitalTwilioSmsNumber,
  onChangeNotificationPreferences,
}: NotificationSettingsProps) => {
  const { translation } = useTranslationContext();
  const isSmsEnabled = enabled && enabledInExternalSystem;

  const onSmsToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeNotificationPreferences(event);
  };

  const onChatNotificationsToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeNotificationPreferences(event, NotificationType.CHAT_MESSAGE_NEW);
  };

  const onCareProgramNotificationsToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeNotificationPreferences(event, NotificationType.MEMBER_NOTIFICATION_EVENT);
  };

  return (
    <StyledContainer disableGutters disablePadding>
      <DisabledOverlay disablePadding enabledInExternalSystem={enabledInExternalSystem}>
        <StyledListItem>
          <Typography variant="body1" color="text.primary">
            {translation('sms_notifications')}
          </Typography>
        </StyledListItem>
        <ListItem sx={{ gap: 4 }}>
          {memberLoaded && (
            <Stack spacing={4} width="100%">
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography id="notification__enable-sms-notification" variant="body2">
                  {memberSmsEnabled
                    ? translation('message_and_data_rates_sms_settings_page')
                    : translation('sms_subtext')}
                </Typography>
                <Switch
                  inputProps={{ 'aria-checked': enabled, 'aria-label': 'Enable SMS notifications' }}
                  onChange={onSmsToggle}
                  checked={isSmsEnabled}
                />
              </Stack>
              {memberSmsEnabled && (
                <Stack spacing={4} pl={6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" color="text.primary">
                      {translation('sms_receive_chat_notifications')}
                    </Typography>
                    <Switch
                      inputProps={{ 'aria-label': 'Receive chat notifications' }}
                      onChange={onChatNotificationsToggle}
                      checked={isSmsEnabled && chatNotificationsEnabled}
                    />
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" color="text.primary">
                      {translation('sms_receive_care_program_notifications')}
                    </Typography>
                    <Switch
                      inputProps={{
                        'aria-label': 'Receive care program and account notifications',
                      }}
                      onChange={onCareProgramNotificationsToggle}
                      checked={isSmsEnabled && memberNotificationEnabled}
                    />
                  </Stack>
                </Stack>
              )}
              <PhoneInputStack
                role="presentation"
                aria-describedby="notification__phone-number-description"
                spacing={1}
              >
                <PhoneInput
                  disabled
                  fullWidth
                  id="notification__user-phone-number-disabled"
                  placeholder="(000)-000-0000"
                  defaultValue={userPhoneNumber}
                  label={translation('phone_number')}
                />
                <Typography id="notification__phone-number-description" variant="caption">
                  {translation('sms_caption_phone_number')}
                </Typography>
              </PhoneInputStack>
            </Stack>
          )}
        </ListItem>
      </DisabledOverlay>

      {!enabledInExternalSystem && (
        <Footnote variant="body2">
          {translation('sms_text_start_1')}
          <Link
            href={`sms:${hospitalTwilioSmsNumber}?body=START`}
            underline="always"
            color="primary"
          >
            {translation('sms_text_start_2', { arg1: hospitalTwilioSmsNumber })}
          </Link>
        </Footnote>
      )}
    </StyledContainer>
  );
};
